import React, {createContext, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useAuth} from "./AuthContext";
import api from "../lib/api";
import {logEvent} from "../analytics/events";

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// Delays before each businesses fetch attempt (initial + 2 retries) for transient failures.
const FETCH_ATTEMPT_DELAYS = [0, 500, 1500];

export type BusinessSummary = {
    id: string;
    name: string;
    imageUrl?: string;
    /** The roles the user holds at this business, not their account-wide roles. */
    roles?: string[];
    /** The permissions the user was granted at this business. */
    permissions?: string[];
};

type BusinessCtx = {
    businesses: BusinessSummary[];
    activeBusiness: BusinessSummary | null;
    isReady: boolean;
    /** Non-null when the last businesses fetch failed (network/401/timeout). */
    error: unknown | null;

    refreshBusinesses: () => Promise<BusinessSummary[]>;
    selectBusiness: (businessId: string) => void;
    clearBusiness: () => void;
};

const ACTIVE_BIZ_KEY = "active_business_id";

const BusinessContext = createContext<BusinessCtx>({} as BusinessCtx);
export const useBusiness = () => useContext(BusinessContext);

async function fetchMyBusinesses(): Promise<BusinessSummary[]> {
    const {data} = await api.get("/api/v1/users/businesses");
    // Accept a bare array, an {items:[]} envelope, or a Spring Data Page ({content:[]}).
    const items = Array.isArray(data)
        ? data
        : (data?.items ?? data?.content ?? null);
    if (items == null) {
        // Dev-only: surface a shape mismatch instead of silently returning empty.
        // Not logged in prod to avoid dumping an API response body to the console.
        if (data && import.meta.env?.DEV) {
            console.warn("[business] unexpected /users/businesses response shape", data);
        }
        return [];
    }
    return items.map((b: any) => ({
        id: String(b.id),
        name: String(b.name ?? b.displayName ?? "Business"),
        imageUrl: b.imageUrl ? String(b.imageUrl) : undefined,
        roles: Array.isArray(b.roles) ? b.roles.map(String) : undefined,
        permissions: Array.isArray(b.permissions) ? b.permissions.map(String) : undefined
    }));
}

function readActiveBusinessId(): string | null {
    return localStorage.getItem(ACTIVE_BIZ_KEY);
}

function writeActiveBusinessId(id: string) {
    localStorage.setItem(ACTIVE_BIZ_KEY, id);
}

function clearActiveBusinessId() {
    localStorage.removeItem(ACTIVE_BIZ_KEY);
}

export function BusinessProvider({children}: { children: React.ReactNode }) {
    const {user, isReady: authReady, isAuthenticated} = useAuth();

    const [businesses, setBusinesses] = useState<BusinessSummary[]>([]);
    const [activeBusiness, setActiveBusiness] = useState<BusinessSummary | null>(null);
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState<unknown | null>(null);

    const hydrating = useRef(false);

    const applyActive = (list: BusinessSummary[], preferredId?: string | null) => {
        if (!list.length) {
            setActiveBusiness(null);
            clearActiveBusinessId();
            return;
        }

        // 1) preferred id
        const preferred = preferredId ? list.find((b) => b.id === preferredId) : null;
        // 2) existing active still valid
        const existing = activeBusiness ? list.find((b) => b.id === activeBusiness.id) : null;

        const chosen = preferred ?? existing ?? (list.length === 1 ? list[0] : null);

        if (chosen) {
            setActiveBusiness(chosen);
            writeActiveBusinessId(chosen.id);
        } else {
            setActiveBusiness(null);
        }
    };

    const applyList = (list: BusinessSummary[]) => {
        setBusinesses(list);
        // If the stored active id no longer exists, applyActive falls back.
        applyActive(list, readActiveBusinessId());
    };

    // Manual retry entry point (used by the Retry/Refresh buttons). Never throws — a
    // failure is surfaced via the `error` state so the UI can offer another retry.
    const refreshBusinesses = async () => {
        try {
            const list = await fetchMyBusinesses();
            applyList(list);
            setError(null);
            return list;
        } catch (e) {
            console.warn("[business] manual refresh failed", e);
            logEvent("business_list_load_failed", {status: (e as any)?.response?.status ?? 0});
            setError(e);
            return businesses;
        } finally {
            setIsReady(true);
        }
    };

    // Hydrate businesses after auth is ready + user exists + tokens are set.
    // Retries transient failures with backoff so a network blip or a 401 mid-refresh
    // doesn't leave the hub stuck on an empty list.
    useEffect(() => {
        let cancelled = false;

        (async () => {
            if (!authReady) return;

            // Mid-login: tokens are set but the user is still loading. Stay not ready
            // instead of reporting an empty list for the new session.
            if (isAuthenticated && !user) {
                setIsReady(false);
                return;
            }

            hydrating.current = true;
            try {
                if (!user || !isAuthenticated) {
                    setBusinesses([]);
                    setActiveBusiness(null);
                    clearActiveBusinessId();
                    setError(null);
                    setIsReady(true);
                    return;
                }

                // A logout left isReady true with an empty list. Without resetting it, an
                // in-tab re-login reads as "no businesses" until the fetch below completes.
                setIsReady(false);

                let lastErr: unknown = null;
                for (let attempt = 0; attempt < FETCH_ATTEMPT_DELAYS.length; attempt++) {
                    if (FETCH_ATTEMPT_DELAYS[attempt]) await sleep(FETCH_ATTEMPT_DELAYS[attempt]);
                    if (cancelled) return; // a newer login/logout superseded this run
                    try {
                        const list = await fetchMyBusinesses();
                        if (cancelled) return;
                        applyList(list);
                        setError(null);
                        lastErr = null;
                        break;
                    } catch (e) {
                        lastErr = e;
                    }
                }

                if (!cancelled && lastErr) {
                    console.warn("[business] failed to load businesses after retries", lastErr);
                    logEvent("business_list_load_failed", {
                        status: (lastErr as any)?.response?.status ?? 0,
                    });
                    setError(lastErr);
                    // Keep any previously loaded list; do not clobber it to empty on error.
                }
            } finally {
                if (!cancelled) {
                    hydrating.current = false;
                    setIsReady(true);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authReady, isAuthenticated, user?.id]);

    // Cross-tab sync for business selection
    useEffect(() => {
        const onStorage = () => {
            if (hydrating.current) return;
            const stored = readActiveBusinessId();
            if (!stored) {
                setActiveBusiness(null);
                return;
            }
            const match = businesses.find((b) => b.id === stored) ?? null;
            setActiveBusiness(match);
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, [businesses]);

    const selectBusiness = (businessId: string) => {
        const match = businesses.find((b) => b.id === businessId) ?? null;
        if (!match) return;
        setActiveBusiness(match);
        writeActiveBusinessId(match.id);
    };

    const clearBusiness = () => {
        setActiveBusiness(null);
        clearActiveBusinessId();
    };

    const value = useMemo<BusinessCtx>(
        () => ({businesses, activeBusiness, isReady, error, refreshBusinesses, selectBusiness, clearBusiness}),
        [businesses, activeBusiness, isReady, error]
    );

    return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
}
